import { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { apiPrivate, API_BASE, getAccessToken } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { FileText, Download, Filter, Calendar, MapPin, Video, ChevronDown, ChevronRight, CheckCircle2, XCircle, UserCheck, Eye, ExternalLink, Clock } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const CATEGORY_LABEL = {
    convocazioni: "Convocazioni",
    bilanci_esercizio: "Bilanci di esercizio",
    statuto_sociale: "Statuto sociale",
    certificati: "Certificati",
    verbali_assemblea: "Verbali Assemblea",
    verbali_cda: "Verbali CDA",
    comunicazioni: "Comunicazioni",
    altro: "Altro",
};

const fmtDate = (iso, withTime = true) => {
    if (!iso) return "—";
    try {
        return withTime
            ? new Date(iso).toLocaleString("it-IT", { dateStyle: "medium", timeStyle: "short" })
            : new Date(iso).toLocaleDateString("it-IT", { dateStyle: "medium" });
    } catch { return iso; }
};

export default function Documenti() {
    const { user } = useAuth();
    const [docs, setDocs] = useState([]);
    const [assemblies, setAssemblies] = useState([]);
    const [filter, setFilter] = useState("all");
    const [activeTab, setActiveTab] = useState("group");

    const reload = () => {
        apiPrivate.get("/documents").then(r => setDocs(r.data)).catch(() => {});
        apiPrivate.get("/assemblies").then(r => setAssemblies(r.data)).catch(() => {});
    };
    useEffect(() => { reload(); }, []);

    const individualDocs = docs.filter(d => d.visibility_type === "individual");
    const groupDocs = docs.filter(d => d.visibility_type === "group");
    const filterFn = (d) => filter === "all" || d.category === filter;
    const groupFiltered = groupDocs.filter(filterFn);
    const individualFiltered = individualDocs.filter(filterFn);

    // Se l'utente cambia filtro e il tab attivo è vuoto ma l'altro no, salto sull'altro
    useEffect(() => {
        if (activeTab === "group" && groupFiltered.length === 0 && individualFiltered.length > 0) {
            setActiveTab("individual");
        } else if (activeTab === "individual" && individualFiltered.length === 0 && groupFiltered.length > 0) {
            setActiveTab("group");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, docs.length]);

    // Split assemblee: attive (in futuro, non closed) vs storico (chiuse o passate)
    const now = new Date();
    const activeAssemblies = assemblies.filter(a => a.status !== "closed" && new Date(a.date_time) >= now);
    const pastAssemblies = assemblies.filter(a => a.status === "closed" || new Date(a.date_time) < now);
    const showConvocazioni = filter === "all" || filter === "convocazioni";

    const handleDownload = async (doc) => {
        try {
            const res = await apiPrivate.get(`/documents/${doc.id}/download-url`);
            const { url, mode, filename } = res.data;
            if (mode === "presigned") {
                // Presigned S3: nuovo <a href download> senza Authorization header
                const a = document.createElement("a");
                a.href = url;
                a.download = filename || doc.filename || "documento";
                a.rel = "noopener noreferrer";
                document.body.appendChild(a);
                a.click();
                a.remove();
            } else {
                // Local stream fallback
                const blobRes = await apiPrivate.get(`/documents/${doc.id}/download`, { responseType: "blob" });
                const blobUrl = window.URL.createObjectURL(blobRes.data);
                const a = document.createElement("a");
                a.href = blobUrl;
                a.download = filename || doc.filename || "documento";
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(blobUrl);
            }
            reload();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore durante il download");
        }
    };

    return (
        <AppLayout>
            <div className="max-w-[1400px] mx-auto" data-testid="documenti-page">
                <span className="label-tag">— Archivio</span>
                <h1 className="font-display text-5xl lg:text-6xl mt-3 tracking-tighter">I tuoi <span className="font-serif-italic text-acid">documenti</span>.</h1>
                <p className="text-zinc-400 mt-4 max-w-xl">Convocazioni, certificati, verbali e comunicazioni ufficiali.</p>

                <div className="mt-10 flex items-center gap-3 flex-wrap" data-testid="docs-filters">
                    <Filter className="h-4 w-4 text-zinc-500" />
                    {[["all", "Tutti"], ...Object.entries(CATEGORY_LABEL)].map(([k, lbl]) => (
                        <button
                            key={k}
                            onClick={() => setFilter(k)}
                            data-testid={`filter-${k}`}
                            className={`text-xs uppercase tracking-widest font-grotesk px-3 py-2 border transition ${filter === k ? "border-acid text-acid" : "border-zinc-800 text-zinc-500 hover:text-white"}`}
                        >
                            {lbl}
                        </button>
                    ))}
                </div>

                {/* Convocazioni ATTIVE (in alto, sempre visibili se filter=all|convocazioni) */}
                {showConvocazioni && activeAssemblies.length > 0 && (
                    <div className="mt-10" data-testid="convocazioni-active-section">
                        <h2 className="text-2xl font-display tracking-tighter flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full bg-magenta animate-pulse" /> Convocazioni attive
                        </h2>
                        <div className="space-y-3 mt-4">
                            {activeAssemblies.map(a => <ConvocazioneCard key={a.id} assembly={a} onChange={reload} />)}
                        </div>
                    </div>
                )}

                <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-10" data-testid="docs-tabs">
                    <TabsList className="bg-transparent border-b border-zinc-900 rounded-none w-full justify-start h-auto p-0">
                        <TabsTrigger value="group" data-testid="tab-group" className="data-[state=active]:bg-transparent data-[state=active]:text-acid data-[state=active]:border-acid rounded-none border-b-2 border-transparent text-zinc-500 font-grotesk uppercase tracking-widest text-xs px-6 py-4">
                            Gruppo quota {user?.quota_categories?.join(" · ") || ""} ({groupFiltered.length})
                        </TabsTrigger>
                        <TabsTrigger value="individual" data-testid="tab-individual" className="data-[state=active]:bg-transparent data-[state=active]:text-acid data-[state=active]:border-acid rounded-none border-b-2 border-transparent text-zinc-500 font-grotesk uppercase tracking-widest text-xs px-6 py-4">
                            I miei documenti ({individualFiltered.length})
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="group" className="mt-8">
                        <DocList docs={groupFiltered} onDownload={handleDownload} />
                    </TabsContent>
                    <TabsContent value="individual" className="mt-8">
                        <DocList docs={individualFiltered} onDownload={handleDownload} />
                    </TabsContent>
                </Tabs>

                {/* Storico convocazioni (in fondo alla pagina, sempre visibile se filter=all|convocazioni) */}
                {showConvocazioni && pastAssemblies.length > 0 && (
                    <div className="mt-14 pt-8 border-t border-zinc-900" data-testid="convocazioni-storico-section">
                        <h3 className="text-lg font-display tracking-tighter text-zinc-400">Storico convocazioni</h3>
                        <div className="space-y-2 mt-3">
                            {pastAssemblies.map(a => <StoricoRow key={a.id} assembly={a} allDocs={docs} onDownload={handleDownload} />)}
                        </div>
                    </div>
                )}
            </div>
        </AppLayout>
    );
}

function ConvocazioneCard({ assembly, onChange }) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [myRsvp, setMyRsvp] = useState(null);
    const [myView, setMyView] = useState(assembly.my_view);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        apiPrivate.get(`/assemblies/${assembly.id}/my-rsvp`).then(r => setMyRsvp(r.data)).catch(() => {});
        // registra la visualizzazione della convocazione
        apiPrivate.post(`/assemblies/${assembly.id}/mark-viewed`).then(r => setMyView(r.data)).catch(() => {});
    }, [assembly.id]);

    const modeLabel = { presenza: "In presenza", remoto: "Solo remoto", ibrida: "Ibrida (presenza + remoto)" }[assembly.mode] || assembly.mode;

    // Deadline effettiva (default = data assemblea)
    const deadlineIso = assembly.rsvp_deadline || assembly.date_time;
    const deadlineDate = deadlineIso ? new Date(deadlineIso) : null;
    const isExpired = deadlineDate ? Date.now() > deadlineDate.getTime() : false;
    const isCustomDeadline = !!assembly.rsvp_deadline;

    const quickRsvp = async (status) => {
        if (isExpired) return;
        if (status === "delegato") { navigate(`/app/assemblee/${assembly.id}`); return; }
        setSaving(true);
        try {
            const r = await apiPrivate.put(`/assemblies/${assembly.id}/rsvp`, { status });
            setMyRsvp(r.data);
            toast.success("Risposta salvata");
            onChange?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Errore");
        } finally { setSaving(false); }
    };

    const downloadPdf = async () => {
        try {
            const token = getAccessToken();
            const res = await fetch(`${API_BASE}/assemblies/${assembly.id}/convocation-pdf`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
            });
            if (!res.ok) throw new Error();
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = assembly.convocation_pdf_filename || `convocazione-${assembly.id.slice(0, 8)}.pdf`; a.click();
            window.URL.revokeObjectURL(url);
            // ri-refresh view timestamp
            apiPrivate.post(`/assemblies/${assembly.id}/mark-viewed`).then(r => setMyView(r.data));
        } catch { toast.error("Errore download PDF"); }
    };

    const downloadIcs = async () => {
        try {
            const token = getAccessToken();
            const res = await fetch(`${API_BASE}/assemblies/${assembly.id}/ics`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: "include",
            });
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `assemblea-${assembly.id.slice(0, 8)}.ics`; a.click();
            window.URL.revokeObjectURL(url);
        } catch { toast.error("Errore download .ics"); }
    };

    return (
        <div className="bento p-6 border-l-4 border-l-magenta" data-testid={`convocazione-${assembly.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-bayside/15 text-bayside">Aperta</span>
                        <span className="px-2 py-0.5 rounded-full text-xs bg-magenta/15 text-magenta">{assembly.type}</span>
                        <span className="text-xs text-zinc-500">Pubblicata il {fmtDate(assembly.published_at)}</span>
                    </div>
                    <h3 className="text-2xl font-display tracking-tighter mt-2">{assembly.title}</h3>
                    <div className="flex items-center gap-4 mt-2 text-sm text-zinc-400 flex-wrap">
                        <span className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-magenta" /> {fmtDate(assembly.date_time)}</span>
                        <span>·</span>
                        <span>{modeLabel}</span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-zinc-500 flex-wrap">
                        {assembly.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {assembly.location}</span>}
                        {assembly.videocall_url && <span className="flex items-center gap-1"><Video className="h-3 w-3" /> Link videocall</span>}
                    </div>
                </div>
            </div>

            {/* Agenda espandibile */}
            {assembly.agenda?.length > 0 && (
                <button onClick={() => setExpanded(!expanded)} className="mt-4 text-xs text-zinc-400 hover:text-white flex items-center gap-1" data-testid={`agenda-toggle-${assembly.id}`}>
                    {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    Ordine del giorno ({assembly.agenda.length} punti)
                </button>
            )}
            {expanded && (
                <ol className="mt-3 space-y-1 pl-6 text-sm text-zinc-300">
                    {assembly.agenda.map((a, i) => (
                        <li key={a.id} className="list-decimal">{a.title}{a.description && <div className="text-xs text-zinc-500 mt-1">{a.description}</div>}</li>
                    ))}
                </ol>
            )}

            {/* RSVP inline */}
            <div className="mt-5 pt-5 border-t border-zinc-800">
                <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <div className="text-xs text-zinc-500 uppercase tracking-widest">La tua risposta</div>
                    {deadlineDate && (
                        <div
                            className={`text-[11px] font-grotesk flex items-center gap-1 ${isExpired ? "text-vermillion" : "text-zinc-500"}`}
                            data-testid={`conv-rsvp-deadline-${assembly.id}`}
                        >
                            <Clock className="h-3 w-3" />
                            {isExpired
                                ? "Termine scaduto"
                                : (isCustomDeadline
                                    ? <>Rispondi entro <span className="text-white ml-1">{fmtDate(deadlineIso)}</span></>
                                    : <>Rispondi entro il giorno dell'assemblea</>
                                )}
                        </div>
                    )}
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => quickRsvp("presente")} disabled={saving || isExpired} className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "presente" ? "border-bayside bg-bayside/10 text-bayside" : "border-zinc-800 text-zinc-400 hover:border-bayside"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`} data-testid={`rsvp-presente-${assembly.id}`}>
                        <CheckCircle2 className="h-4 w-4 inline mr-1.5" /> Sarò presente
                    </button>
                    <button onClick={() => quickRsvp("delegato")} disabled={saving || isExpired} className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "delegato" ? "border-magenta bg-magenta/10 text-magenta" : "border-zinc-800 text-zinc-400 hover:border-magenta"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`} data-testid={`rsvp-delegato-${assembly.id}`}>
                        <UserCheck className="h-4 w-4 inline mr-1.5" /> {myRsvp?.status === "delegato" ? `Delegato: ${myRsvp.delegate_display}` : "Delega"}
                    </button>
                    <button onClick={() => quickRsvp("assente")} disabled={saving || isExpired} className={`px-4 py-2 rounded-full text-sm border-2 transition ${myRsvp?.status === "assente" ? "border-vermillion bg-vermillion/10 text-vermillion" : "border-zinc-800 text-zinc-400 hover:border-vermillion"} ${isExpired ? "opacity-40 cursor-not-allowed" : ""}`} data-testid={`rsvp-assente-${assembly.id}`}>
                        <XCircle className="h-4 w-4 inline mr-1.5" /> Sarò assente
                    </button>
                </div>
                {myRsvp && <p className="text-xs text-zinc-500 mt-2">Ultima modifica: {fmtDate(myRsvp.updated_at)}</p>}
            </div>

            {/* Azioni */}
            <div className="mt-5 pt-5 border-t border-zinc-800 flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 flex-wrap">
                    {assembly.convocation_pdf_key && (
                        <button onClick={downloadPdf} className="btn-ghost text-xs" data-testid={`download-pdf-${assembly.id}`}>
                            <Download className="h-3 w-3" /> Convocazione PDF
                        </button>
                    )}
                    <button onClick={downloadIcs} className="btn-ghost text-xs" data-testid={`download-ics-${assembly.id}`}>
                        <Calendar className="h-3 w-3" /> Aggiungi al calendario (.ics)
                    </button>
                    <button onClick={() => navigate(`/app/assemblee/${assembly.id}`)} className="btn-ghost text-xs">
                        <ExternalLink className="h-3 w-3" /> Dettaglio completo
                    </button>
                </div>
                {myView && (
                    <div className="flex items-center gap-1 text-xs text-zinc-500" title={`Prima visualizzazione: ${fmtDate(myView.first_viewed_at)} · Aperture: ${myView.viewed_count}`}>
                        <Eye className="h-3 w-3" /> Visualizzata il {fmtDate(myView.last_viewed_at)}
                    </div>
                )}
            </div>
        </div>
    );
}

function StoricoRow({ assembly, allDocs, onDownload }) {
    const navigate = useNavigate();
    const verbale = assembly.verbale_document_id ? allDocs.find(d => d.id === assembly.verbale_document_id) : null;
    return (
        <div className="bento p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`storico-${assembly.id}`}>
            <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full text-xs bg-zinc-700/40 text-zinc-400">Chiusa</span>
                    <span className="text-xs text-zinc-500">{fmtDate(assembly.date_time, false)}</span>
                </div>
                <div className="font-semibold text-sm mt-1">{assembly.title}</div>
            </div>
            <div className="flex gap-2 flex-wrap items-center">
                {verbale ? (
                    <button
                        onClick={() => onDownload?.(verbale)}
                        className="btn-ghost text-xs text-bayside"
                        data-testid={`verbale-download-${assembly.id}`}
                        title={verbale.title}
                    >
                        <Download className="h-3 w-3" /> Verbale
                    </button>
                ) : (
                    <span className="text-xs text-zinc-600 italic">Verbale non ancora disponibile</span>
                )}
                <button onClick={() => navigate(`/app/assemblee/${assembly.id}`)} className="btn-ghost text-xs">Dettaglio</button>
            </div>
        </div>
    );
}

function DocList({ docs, onDownload }) {
    if (docs.length === 0) {
        return <div className="bento p-12 text-center text-zinc-500" data-testid="docs-empty">Nessun documento in questa sezione.</div>;
    }
    return (
        <div className="space-y-2" data-testid="docs-list">
            {docs.map(d => (
                <div key={d.id} className="bento p-5 flex items-center justify-between gap-4 hover:border-zinc-700 transition" data-testid={`doc-row-${d.id}`}>
                    <div className="flex items-start gap-4 min-w-0 flex-1">
                        <FileText className="h-5 w-5 text-zinc-500 mt-1 shrink-0" />
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-3 flex-wrap">
                                <span className="font-grotesk font-medium truncate">{d.title}</span>
                                {!d.is_read && <span className="bg-acid text-black text-[10px] font-bold px-2 py-0.5" data-testid="new-badge">NUOVO</span>}
                            </div>
                            <div className="text-zinc-500 text-xs mt-1 uppercase tracking-widest font-grotesk">
                                {CATEGORY_LABEL[d.category] || d.category} · {fmtDate(d.published_at, false)} · {(d.size / 1024).toFixed(0)} KB
                            </div>
                            {d.description && <div className="text-zinc-500 text-sm mt-2">{d.description}</div>}
                        </div>
                    </div>
                    <button onClick={() => onDownload(d)} className="btn-ghost shrink-0" data-testid={`download-${d.id}`}>
                        <Download className="h-4 w-4" /> Scarica
                    </button>
                </div>
            ))}
        </div>
    );
}
